// Тип текущей строки для строки состояния: «Абзац», «Перечисление «-», уровень 1», «Подпись рисунка»… и её
// оформление в DOCX (подсказка) - чтобы было понятно, что именно сейчас пишется и как это выйдет в отчёте.
// Малые редакторы (задание, реферат, раздел об ИИ, сноска) сообщают о фокусе через setLineFocus().
import type { Editor } from '@tiptap/core';
import type { EditorState } from '@tiptap/pm/state';
import { NodeSelection } from '@tiptap/pm/state';

export interface LineKind { kind: string; label: string; fmt: string }

const BODY = 'Times New Roman 14 пт, интервал 1,5, по ширине, абзацный отступ 1,25 см';
const FMT: Record<string, string> = {
  para: BODY,
  h1: 'Полужирный 14 пт, с абзацного отступа, номер ставится сам; раздел - с новой страницы, после - 12 пт',
  h2: 'Полужирный 14 пт, с абзацного отступа, номер «1.1» ставится сам; 12 пт до и после',
  h3: 'Полужирный 14 пт, с абзацного отступа, номер «1.1.1» ставится сам; 12 пт до и после',
  list: 'Маркер с абзацного отступа 1,25 см, перенос строки - к левому полю; позиция со строчной буквы, в конце «;», в последней «.» (4.4)',
  list2: 'Вложенный уровень: маркер с отступа 2,5 см, перенос строки - к левому полю (4.4.4)',
  figcap: 'Под рисунком по центру, 12 пт, одинарный: «Рисунок N. Название» без точки в конце (4.5)',
  tblcap: 'Над таблицей слева без отступа, 14 пт: «Таблица N. Название» без точки в конце (4.9)',
  lstcap: 'Над листингом слева без отступа, 14 пт: «Листинг N. Название» (6.1)',
  cell: 'Текст таблицы 12 пт, одинарный; головка - по центру (4.9)',
  tnote: 'Примечание к таблице - в последней строке таблицы, 12 пт (4.9)',
  note: BODY + '; «Примечание – …» / «Примечания» и «1 …» (4.10)',
  example: BODY + '; «Пример – …», 12 пт до и после (4.15)',
  app: 'По центру, полужирный: «Приложение А», статус, название (4.16)',
  struct: 'По центру, полужирный, без номера; с новой страницы',
  footnote: 'Текст сноски: 10 пт, одинарный, с абзацного отступа (4.10.9)',
};

function listLevelAndKind(state: EditorState): { level: number; kind: string } {
  const { $from } = state.selection;
  let level = 0; let top = ''; let kind = '';
  for (let d = $from.depth; d > 0; d--) {
    const n = $from.node(d);
    if (n.type.name === 'stoList') {
      level++;
      if (!kind) kind = n.attrs.kind;
      top = n.attrs.kind;
    }
  }
  let k = kind;
  if (k === 'auto') k = level > 1 && (top === 'letter' || top === 'digit') ? 'digit' : 'dash';
  return { level, kind: k };
}

/** Что сейчас под курсором. */
export function describeLine(state: EditorState): LineKind {
  const sel = state.selection;
  if (sel instanceof NodeSelection) {
    const t = sel.node.type.name;
    const names: Record<string, string> = { figure: 'Рисунок', stoTable: 'Таблица', formula: 'Формула', listing: 'Листинг',
      pageBreak: 'Разрыв страницы', structHeading: 'Структурный элемент', mathInline: 'Формула в строке', footnote: 'Сноска',
      cite: 'Ссылка на источник', xref: 'Перекрёстная ссылка', image: 'Рисунок' };
    return { kind: 'node', label: names[t] || 'Объект', fmt: '' };
  }
  const { $from } = sel;
  const parent = $from.parent.type.name;
  const up = (name: string) => { for (let d = $from.depth; d > 0; d--) if ($from.node(d).type.name === name) return $from.node(d); return null; };
  if (parent === 'heading') {
    const l = $from.parent.attrs.level || 1;
    const lbl = ['', 'Заголовок раздела', 'Заголовок подраздела', 'Заголовок пункта'][l] || 'Заголовок';
    return { kind: 'h' + l, label: lbl, fmt: FMT['h' + l] || FMT.h1 };
  }
  if (parent === 'appendixTitle') return { kind: 'app', label: 'Название приложения', fmt: FMT.app };
  if (parent === 'caption') {
    const obj = $from.node($from.depth - 1)?.type.name;
    if (obj === 'stoTable') return { kind: 'tblcap', label: 'Название таблицы', fmt: FMT.tblcap };
    if (obj === 'listing') return { kind: 'lstcap', label: 'Название листинга', fmt: FMT.lstcap };
    return { kind: 'figcap', label: 'Подпись рисунка', fmt: FMT.figcap };
  }
  if (up('tableNote')) return { kind: 'tnote', label: 'Примечание к таблице', fmt: FMT.tnote };
  if (up('tableHeader')) return { kind: 'cell', label: 'Головка таблицы', fmt: FMT.cell };
  if (up('tableCell')) return { kind: 'cell', label: 'Ячейка таблицы', fmt: FMT.cell };
  if (up('stoListItem')) {
    const { level, kind } = listLevelAndKind(state);
    const mk = kind === 'letter' ? '«а)»' : kind === 'digit' ? '«1)»' : '«-»';
    return { kind: 'list', label: `Перечисление ${mk}, уровень ${level}`, fmt: level > 1 ? FMT.list2 : FMT.list };
  }
  if (up('note')) return { kind: 'note', label: 'Примечание', fmt: FMT.note };
  if (up('example')) return { kind: 'example', label: 'Пример', fmt: FMT.example };
  if (parent === 'paragraph') return { kind: 'para', label: 'Абзац', fmt: FMT.para };
  return { kind: parent, label: 'Текст', fmt: '' };
}

// ------------------------------------------------------------------------------------------ фокус малых редакторов
let focus: { editor: Editor | null; where: string } = { editor: null, where: '' };
const subs = new Set<() => void>();

export function setLineFocus(editor: Editor | null, where: string) {
  if (focus.editor === editor && focus.where === where && editor) { subs.forEach((f) => f()); return; }
  focus = { editor, where };
  subs.forEach((f) => f());
}
export function lineFocus() { return focus; }
export function onLineFocus(f: () => void): () => void { subs.add(f); return () => { subs.delete(f); }; }

/** Тип строки с учётом малого редактора в фокусе (для строки состояния). */
export function currentLine(main: Editor | null): (LineKind & { where: string }) | null {
  const f = focus;
  if (f.editor && !f.editor.isDestroyed) {
    const k = describeLine(f.editor.state);
    if (f.where === 'Сноска') return { ...k, label: 'Текст сноски', fmt: FMT.footnote, where: '' };
    return { ...k, where: f.where };
  }
  if (!main || main.isDestroyed) return null;
  return { ...describeLine(main.state), where: '' };
}
